import type { ReactNode } from "react";
import { Wordmark } from "../components/ui";
import { SUPPORT_EMAIL } from "../lib/contact";

// Everything the policy says about the company comes from here, so it only
// has to change in one place.
const COMPANY = {
  name: "Mythical Box Ltd",
  number: "15934210",
  address: "91d Grosvenor Road, London, England, SW1V 3LD",
  email: SUPPORT_EMAIL,
} as const;

const LAST_UPDATED = "13 October 2026";

const ContentColumn = ({ children }: { children: ReactNode }) => (
  <div className="mx-auto w-full max-w-2xl">{children}</div>
);

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="flex flex-col gap-3">
    <h2 className="font-lora text-[22px] font-semibold leading-tight text-hazumu-ink">
      {title}
    </h2>
    {children}
  </section>
);

const SubHeading = ({ children }: { children: ReactNode }) => (
  <h3 className="mt-2 font-sans text-[14px] font-semibold text-hazumu-ink">{children}</h3>
);

const List = ({ children }: { children: ReactNode }) => (
  <ul className="flex list-disc flex-col gap-2 pl-5 marker:text-hazumu-clay">{children}</ul>
);

const Item = ({ label, children }: { label?: string; children: ReactNode }) => (
  <li>
    {label && <strong className="font-semibold text-hazumu-ink">{label}: </strong>}
    {children}
  </li>
);

const Link = ({ href, children }: { href: string; children: ReactNode }) => (
  <a
    className="rounded-sm text-hazumu-ink underline decoration-hazumu-clay underline-offset-2 transition-colors hover:decoration-hazumu-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hazumu-ink"
    href={href}
    {...(href.startsWith("http") ? { target: "_blank", rel: "noreferrer" } : {})}
  >
    {children}
  </a>
);

const Email = () => <Link href={`mailto:${COMPANY.email}`}>{COMPANY.email}</Link>;

export const PrivacyPolicyPage = () => (
  <main className="min-h-screen bg-hazumu-canvas font-sans text-hazumu-ink">
    <header className="border-b border-hazumu-border bg-hazumu-canvas">
      <ContentColumn>
        <div className="flex items-center justify-between px-6 py-5">
          <a
            aria-label="Hazumu home"
            className="rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-hazumu-ink"
            href="/"
          >
            <Wordmark size="sm" />
          </a>
          <a
            className="rounded-sm font-sans text-[13px] font-medium text-hazumu-muted transition-colors hover:text-hazumu-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hazumu-ink"
            href="/"
          >
            Back to Hazumu
          </a>
        </div>
      </ContentColumn>
    </header>

    <article>
      <ContentColumn>
        <div className="flex flex-col gap-10 px-6 pb-20 pt-12 text-[14px] leading-[1.7] text-hazumu-muted">
          <div className="flex flex-col gap-3">
            <h1 className="font-lora text-[36px] font-semibold leading-[1.1] text-hazumu-ink">
              Privacy policy
            </h1>
            <p className="text-[12px]">Last updated {LAST_UPDATED}</p>
            <p>
              Hazumu is a website builder for simple, personal sites. This
              policy explains what personal data we collect when you use
              Hazumu or visit a site built with it, why we collect it, who we
              share it with, and the choices and rights you have.
            </p>
          </div>

          <Section title="The short version">
            <List>
              <Item>We collect what we need to run your account, host your site and take payment. Nothing more.</Item>
              <Item>We don&rsquo;t sell your data, show ads, or use analytics or tracking cookies.</Item>
              <Item>We don&rsquo;t use your content to train AI models.</Item>
              <Item>Anything you publish on your site is public, and so are images you upload. Please only upload what you&rsquo;re happy for anyone to see.</Item>
              <Item>You can delete your account at any time, and we&rsquo;ll erase your data.</Item>
            </List>
          </Section>

          <Section title="Who we are">
            <p>
              Hazumu is run by {COMPANY.name}, a company registered in England
              and Wales (company number {COMPANY.number}), whose registered
              office is at {COMPANY.address}. For the personal data described
              in this policy, {COMPANY.name} is the data controller under the
              UK General Data Protection Regulation (UK GDPR) and the Data
              Protection Act 2018. That means we decide how and why your data
              is used and are responsible for protecting it.
            </p>
            <p>
              You can reach us about anything in this policy at <Email />.
            </p>
          </Section>

          <Section title="What we collect">
            <SubHeading>Your account</SubHeading>
            <p>
              When you sign up, we collect your email address and a password.
              Your password is stored as a secure hash, and we can never see
              it. We also keep basic account records such as when you signed
              up and when you last signed in.
            </p>

            <SubHeading>Your sites</SubHeading>
            <p>
              We store everything you put into Hazumu so we can save, preview
              and publish it: site names and descriptions, your chosen
              address, pages, text, links, quotes, video links, design
              settings, and earlier versions of your site. If you include
              personal information in your site, for example your name,
              contact details or a photo of yourself, we store that too.
            </p>

            <SubHeading>Images you upload</SubHeading>
            <p>
              We store the images you upload along with details such as file
              type, size, dimensions and any alt text you add. Uploaded images
              are kept in public storage so they load quickly on your site.
              Anyone who has an image&rsquo;s link can view it, even if the
              page it sits on isn&rsquo;t published yet.
            </p>

            <SubHeading>Your subscription</SubHeading>
            <p>
              When you subscribe, our payment provider Polar handles the
              payment. We never see or store your full card details. Polar
              sends us your customer and subscription reference numbers,
              whether your subscription is active and when it renews, so we
              know which features to switch on.
            </p>

            <SubHeading>Custom domains</SubHeading>
            <p>
              If you connect your own domain, we store the domain name and its
              verification status, and register it with Cloudflare so we can
              serve your site on it securely.
            </p>

            <SubHeading>Technical data</SubHeading>
            <p>
              Like any website, our servers and providers receive technical
              information when you use Hazumu or visit a Hazumu site,
              including your IP address, browser and device type, the pages
              requested and the time of the request. We use it to deliver
              pages, keep the service secure, prevent abuse and fix problems.
              We don&rsquo;t use it to build a profile of you.
            </p>

            <SubHeading>Data stored on your device</SubHeading>
            <p>
              We don&rsquo;t use advertising or analytics cookies. Hazumu keeps
              a small amount of data in your browser&rsquo;s local storage
              because the service can&rsquo;t work without it: a sign-in token
              that keeps you logged in, and a temporary copy of unsaved edits
              so you don&rsquo;t lose work if your connection drops. Our hosting
              provider, Cloudflare, may also set strictly necessary security
              cookies to protect against attacks. Because all of this is
              strictly necessary, we don&rsquo;t ask for consent. You can clear
              it at any time in your browser settings, but that will sign you
              out.
            </p>
          </Section>

          <Section title="Visiting a site built with Hazumu">
            <p>
              If you&rsquo;re visiting a site that someone built with Hazumu,
              we process the technical data described above to deliver that
              site to you. We don&rsquo;t add analytics or tracking to the
              sites we host.
            </p>
            <p>
              The content of each site belongs to its owner, who is
              responsible for any personal information they choose to publish.
              If a site includes information about you that you want changed
              or removed, please contact the site owner first. If you
              can&rsquo;t reach them, or you believe content breaks the law,
              email us at <Email />.
            </p>
            <p>
              Some sites include embedded videos from YouTube or Vimeo. We use
              YouTube&rsquo;s privacy-enhanced mode and Vimeo&rsquo;s
              &ldquo;do not track&rdquo; setting, but when a video loads your
              browser connects directly to that provider, which receives your
              IP address. Their own privacy policies apply.
            </p>
          </Section>

          <Section title="Why we use your data">
            <p>
              Data protection law requires a legal basis for each way we use
              personal data. Ours are:
            </p>
            <List>
              <Item label="To provide Hazumu (contract)">
                Creating and securing your account, saving and publishing your
                sites, serving your custom domain, handling your subscription,
                and sending the emails the service needs, such as sign-up
                confirmation and password resets.
              </Item>
              <Item label="To keep Hazumu safe and working (legitimate interests)">
                Preventing fraud, spam and abuse, rate-limiting requests,
                diagnosing errors, and keeping the service secure and
                reliable. We&rsquo;ve weighed these uses against your rights
                and kept them to the minimum we need.
              </Item>
              <Item label="To meet our legal obligations (legal obligation)">
                Keeping records required by law, and responding to lawful
                requests from authorities.
              </Item>
            </List>
            <p>
              We only send emails about your account and the service. If we
              ever want to send marketing emails, we&rsquo;ll ask first.
            </p>
          </Section>

          <Section title="Who we share it with">
            <p>
              We don&rsquo;t sell your personal data. We share it only with
              the providers who help us run Hazumu, and only as much as each
              one needs:
            </p>
            <List>
              <Item label="Supabase">
                Hosts our database and user accounts, in the European Union
                (Frankfurt, Germany).
              </Item>
              <Item label="Cloudflare">
                Hosts and delivers Hazumu and the sites built with it from
                servers around the world, stores uploaded images in the
                European Union, manages custom domains and protects against
                attacks.
              </Item>
              <Item label="Resend">
                Sends our account emails, such as sign-up confirmations and
                password resets.
              </Item>
              <Item label="Polar">
                Our merchant of record. Polar processes subscription payments,
                handles sales tax and issues receipts. It acts as an
                independent controller of your payment information, under{" "}
                <Link href="https://polar.sh/legal/privacy-policy">its own privacy policy</Link>.
              </Item>
              <Item label="Google Fonts">
                Supplies the typefaces used by Hazumu and Hazumu sites. When a
                page loads, your browser requests fonts from Google, which
                receives your IP address.
              </Item>
            </List>
            <p>
              We may also share data if the law requires it, to protect the
              rights, property or safety of our users or others, or with a
              buyer if Hazumu is ever sold or merged. In that case
              we&rsquo;ll tell you in advance, and this policy will continue
              to protect your data.
            </p>
          </Section>

          <Section title="International transfers">
            <p>
              Our main database is in the European Union, which the UK
              recognises as providing adequate protection for personal data.
              Some of our providers, including Cloudflare, Resend, Polar and
              Google, may process data in the United States or other
              countries outside the UK. When they do, we rely on safeguards
              recognised under UK law, such as the UK Extension to the EU-US
              Data Privacy Framework or the UK International Data Transfer
              Addendum to the European Commission&rsquo;s standard contractual
              clauses. You can contact us for more information about these
              safeguards.
            </p>
          </Section>

          <Section title="How long we keep it">
            <List>
              <Item label="Account and site data">
                For as long as you have an account.
              </Item>
              <Item label="Deleted sites">
                Taken offline immediately and erased from our systems within
                30 days.
              </Item>
              <Item label="Deleted accounts">
                All your account data, sites and uploaded images are erased
                within 30 days, including from backups.
              </Item>
              <Item label="Custom domains">
                Disconnected and removed 30 days after a subscription ends.
              </Item>
              <Item label="Technical and security logs">
                Kept for a short period, and never more than 90 days, unless
                we need them longer to investigate a specific security
                incident.
              </Item>
              <Item label="Unsaved edits in your browser">
                Cleared once your changes are saved, or whenever you clear
                your browser data.
              </Item>
            </List>
            <p>
              Polar keeps its own records of payments for as long as the law
              requires.
            </p>
          </Section>

          <Section title="Deleting your account">
            <p>
              You can delete your account at any time from your account
              settings in Hazumu. Your published sites go offline straight
              away, and we erase your data as described above. If you have an
              active subscription, please cancel it first so that you
              aren&rsquo;t charged again.
            </p>
          </Section>

          <Section title="Your rights">
            <p>Under UK data protection law, you have the right to:</p>
            <List>
              <Item>get a copy of the personal data we hold about you</Item>
              <Item>have inaccurate data corrected</Item>
              <Item>have your data erased</Item>
              <Item>restrict how we use your data, or object to our use of it based on legitimate interests</Item>
              <Item>receive your data in a portable format</Item>
            </List>
            <p>
              To use any of these rights, email us at <Email />. We may need
              to confirm your identity first. We&rsquo;ll respond within one
              month. Making a request is free.
            </p>
            <p>
              If you&rsquo;re unhappy with how we&rsquo;ve handled your data,
              please tell us so we can try to put it right. You also have the
              right to complain to the Information Commissioner&rsquo;s Office
              (ICO), the UK&rsquo;s data protection regulator, at{" "}
              <Link href="https://ico.org.uk/make-a-complaint/">ico.org.uk</Link>.
            </p>
          </Section>

          <Section title="Keeping your data secure">
            <p>
              Data is encrypted in transit and at rest. Access to each
              account&rsquo;s data is enforced in the database itself, so one
              user can never read or change another&rsquo;s private data.
              Only the few people who need access to run Hazumu have it. No
              system is perfectly secure, but if a breach ever affects your
              data, we&rsquo;ll tell you and the ICO when the law requires it.
            </p>
          </Section>

          <Section title="Children">
            <p>
              Hazumu is not intended for anyone under 16, and you must be at
              least 16 to create an account. If you think a child under 16
              has given us their data, contact us and we&rsquo;ll delete it.
            </p>
          </Section>

          <Section title="Changes to this policy">
            <p>
              We&rsquo;ll update this policy as Hazumu changes. If we make a
              significant change, we&rsquo;ll let you know by email or in the
              app before it takes effect. The date at the top shows when the
              policy was last updated.
            </p>
          </Section>

          <Section title="Contact us">
            <p>
              Questions, requests or concerns about your privacy: <Email />,
              or write to {COMPANY.name}, {COMPANY.address}.
            </p>
          </Section>
        </div>
      </ContentColumn>
    </article>
  </main>
);
